import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import theme from "../../theme";
import {
  selectAllInstanceCounts,
  selectFields,
  selectPages,
  selectSchemaName,
} from "../../store/selectors/formSelectors";
import { setViewMode } from "../../store/slices/formUiSlice";
import { useTranslation } from "../../utils/OpenAIRE/TranslationContext";
import {
  setActivePage,
  selectActivePage,
} from "../../store/slices/activePageSlice";
import {
  clearChildFormNavigation,
  setChildFormNavigation,
} from "../../store/slices/childFormNavigationSlice";

import {
  hasMandatoryFields,
  hasRecommendedFields,
  hasOptionalFields,
  getDisplayedFields,
  isFieldVisibleInViewMode,
} from "../../utils/viewModeUtils";


const getFieldByPath = (fieldsArray, targetPath) => {
  if (!Array.isArray(fieldsArray)) return null;
  for (const field of fieldsArray) {
    if (field?.path === targetPath) {
      return field;
    }
    if (field?.children?.length) {
      const found = getFieldByPath(field.children, targetPath);
      if (found) return found;
    }
  }
  return null;
};

function FormSidebar({ onNavigate, onClose, isDrawer = false, onCollapse, isDocked = false }) {
  // ALL hooks at the top — no exceptions
  const pages = useSelector(selectPages);
  const activePage = useSelector(selectActivePage);
  const fields = useSelector(selectFields);
  const schemaName = useSelector(selectSchemaName);
  const {
    viewMode,
    currentPage = 1,
    fieldsPerPage = 6,
  } = useSelector((state) => state.formUi || {});
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const childPagesMeta = useSelector((state) => state.fieldSchema.childPages);
  const allInstanceCounts = useSelector(selectAllInstanceCounts);

  const childFormNavigation = useSelector((state) => state.childFormNavigation);

  const selectedPageIndex = childFormNavigation?.childPageIndex ?? activePage;

  const hasSchemaPages = Array.isArray(pages) && pages.length > 0;

  // const currentPageData = pages[activePage];
  // const currentDigest = currentPageData?.captureBaseDigest;

  // Build the active path indices to know which parents are "open"
  const activePathIndices = new Set();
  let currIndex = activePage;
  while (currIndex !== undefined && currIndex !== null) {
    if (activePathIndices.has(currIndex)) break;
    activePathIndices.add(currIndex);
    const meta = childPagesMeta?.[currIndex];
    currIndex = meta?.parentPageIndex;
  }

  const visiblePages = [];
  const safePages = Array.isArray(pages) ? pages : [];
  const pagesWithIndex = safePages.map((page, i) => ({ ...page, index: i }));

  const basePages = pagesWithIndex.filter((p) => !p.isChildPage);

  // Group children by their intended parent page
  const childrenByParent = {};
  pagesWithIndex.forEach((p) => {
    if (p.isChildPage) {
      const meta = childPagesMeta?.[p.index];
      if (meta && meta.parentPageIndex !== undefined) {
        const parentField = getFieldByPath(fields, meta.parentFieldPath);
        if (isFieldVisibleInViewMode(parentField, viewMode)) {
          if (!childrenByParent[meta.parentPageIndex]) {
            childrenByParent[meta.parentPageIndex] = [];
          }
          childrenByParent[meta.parentPageIndex].push(p);
        }
      }
    }
  });

  Object.keys(childrenByParent).forEach((parentIndexStr) => {
    const parentIndex = parseInt(parentIndexStr, 10);
    const parentPage = pages[parentIndex];
    if (parentPage && parentPage.items) {
      const extractPaths = (fieldsArray) => {
        let paths = [];
        fieldsArray.forEach((f) => {
          if (f?.path) paths.push(f.path);
          if (f?.children && Array.isArray(f.children)) {
            paths = paths.concat(extractPaths(f.children));
          }
        });
        return paths;
      };

      const orderedPaths = [];
      parentPage.items.forEach((item) => {
        if (item.type === "field" && item.field) {
          orderedPaths.push(...extractPaths([item.field]));
        } else if (item.type === "section" && item.fields) {
          orderedPaths.push(...extractPaths(item.fields));
        }
      });

      childrenByParent[parentIndex].sort((a, b) => {
        const metaA = childPagesMeta?.[a.index];
        const metaB = childPagesMeta?.[b.index];
        const pathA = metaA?.parentFieldPath;
        const pathB = metaB?.parentFieldPath;

        let idxA = orderedPaths.indexOf(pathA);
        let idxB = orderedPaths.indexOf(pathB);

        if (idxA === -1) idxA = Infinity;
        if (idxB === -1) idxB = Infinity;

        if (idxA !== idxB) {
          return idxA - idxB;
        }
        return a.index - b.index;
      });
    }
  });

  // Recursively build the list so children appear right under their parents
  const addPageAndChildren = (page) => {
    visiblePages.push(page);
    if (activePathIndices.has(page.index)) {
      const children = childrenByParent[page.index] || [];
      children.forEach((child) => addPageAndChildren(child));
    }
  };

  basePages.forEach((basePage) => addPageAndChildren(basePage));

  const isGeneratedChildPage =
    childFormNavigation?.useGeneratedPage &&
    !!childFormNavigation?.fallbackLabel;

  const displayedFields = getDisplayedFields(fields, viewMode);
  const totalFlatPages = Math.ceil(displayedFields.length / fieldsPerPage);

  let resolvedBasePageIndex = selectedPageIndex;
  while (
    resolvedBasePageIndex !== undefined &&
    resolvedBasePageIndex !== null &&
    pages[resolvedBasePageIndex]?.isChildPage
  ) {
    const meta = childPagesMeta?.[resolvedBasePageIndex];
    if (meta?.parentPageIndex !== undefined) {
      resolvedBasePageIndex = meta.parentPageIndex;
    } else {
      break;
    }
  }
  const currentBasePageNumber =
    basePages.findIndex((p) => p.index === resolvedBasePageIndex) + 1;

  const currentDisplayPage = hasSchemaPages
    ? currentBasePageNumber
    : totalFlatPages === 0
      ? 0
      : currentPage;
  const totalDisplayPages = hasSchemaPages ? basePages.length : totalFlatPages;

  const getViewModeChipSx = (modeKey) => ({
    backgroundColor:
      viewMode === modeKey ? theme.primaryColor : theme.backgroundColor,
    color: viewMode === modeKey ? theme.backgroundColor : theme.primaryColor,
    borderColor:
      viewMode === modeKey ? theme.backgroundColor : theme.primaryColor,
    border:
      viewMode === modeKey ? "1px solid transparent" : "1px solid currentColor",
    borderRadius: "16px",
    fontSize: 15,
    fontWeight: 400,
    height: 32,
    cursor: "pointer",
    "&:hover": {
      backgroundColor:
        viewMode === modeKey
          ? theme.primaryColor
          : theme.hoverUnselectedBgColor,
      borderColor: theme.primaryColor,
    },
  });

  const showMandatory = hasMandatoryFields(fields);
  const showRecommended = hasRecommendedFields(fields);
  const showOptional = hasOptionalFields(fields);
  const availableModesCount = [
    showMandatory,
    showRecommended,
    showOptional,
  ].filter(Boolean).length;

  return (
    <Paper
      elevation={isDrawer ? 0 : 2}
      sx={{
        borderRadius: isDrawer ? 0 : 1,
        overflow: "hidden",
        width: 280,
        height: isDrawer ? "100%" : "auto",
        maxHeight: isDrawer ? "100vh" : "calc(100vh - 48px)",
        overflowY: "auto",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* SCHEMA PAGES NAV */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: theme.primaryColor,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.05rem",
            fontWeight: 700,
            color: "#fff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {schemaName || "Form Navigation"}
        </Typography>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#fff",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.15)",
              },
            }}
            aria-label="Close navigation sidebar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        {!onClose && onCollapse && (
          <Tooltip title="Collapse sidebar">
            <IconButton
              onClick={onCollapse}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                },
              }}
              aria-label="Collapse navigation sidebar"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {availableModesCount > 1 && (
        <Box
          sx={{
            px: 2,
            pt: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {showMandatory && (
            <Chip
              label={t("dynamicform.mandatory")}
              clickable
              sx={getViewModeChipSx("mandatory")}
              onClick={() => dispatch(setViewMode("mandatory"))}
            />
          )}
          {showRecommended && (
            <Chip
              label={t("dynamicform.recommended")}
              clickable
              onClick={() => dispatch(setViewMode("recommended"))}
              sx={getViewModeChipSx("recommended")}
            />
          )}
          {showOptional && (
            <Chip
              label={t("dynamicform.complete")}
              clickable
              onClick={() => dispatch(setViewMode("complete"))}
              sx={getViewModeChipSx("complete")}
            />
          )}
        </Box>
      )}
      <Typography variant="body2" px="5px" py="10px" align="center">
        Showing Page {currentDisplayPage} of {totalDisplayPages} Pages
      </Typography>

      <Divider />

      {isGeneratedChildPage ? (
        <List disablePadding>
          <ListItemButton
            selected
            onClick={() => {
              onNavigate?.();
            }}
            sx={{
              "&.Mui-selected": {
                backgroundColor: theme.secondaryColor,
                color: theme.primaryColor,
              },
              "&.Mui-selected:hover": {
                backgroundColor: theme.secondaryColor,
              },
            }}
          >
            <ListItemText primary={childFormNavigation.fallbackLabel} />
          </ListItemButton>
        </List>
      ) : hasSchemaPages ? (
        <List disablePadding>
          {visiblePages.map((page) => {
            const i = page.index;
            const childMeta = childPagesMeta?.[i]; // from fieldSchema.childPages
            const isChildPage = !!page.isChildPage;
            // const depth = page.depth || 0;

            return (
              <ListItemButton
                key={page.id}
                selected={selectedPageIndex === i}
                onClick={() => {
                  const hasActiveChildPage =
                    childFormNavigation?.childPageIndex !== undefined &&
                    childFormNavigation?.childPageIndex !== null;

                  if (hasActiveChildPage) {
                    if (selectedPageIndex === i) {
                      return;
                    }

                    let isSuccessor = false;
                    let curr = i;
                    while (curr !== undefined && curr !== null) {
                      const meta = childPagesMeta?.[curr];
                      if (meta?.parentPageIndex === selectedPageIndex) {
                        isSuccessor = true;
                        break;
                      }
                      curr = meta?.parentPageIndex;
                    }

                    if (!isSuccessor) {
                      const confirmDiscard = window.confirm(
                        "All changes that you have made will get discarded. Do you want to continue?",
                      );
                      if (!confirmDiscard) {
                        return;
                      }
                    }
                  }

                  if (isChildPage) {
                    const parentFieldPath = childMeta?.parentFieldPath;
                    const parentPageIndex = childMeta?.parentPageIndex;
                    const label = childMeta?.label || page.sidebarLabel;

                    let count = 0;
                    let actualParentPath = parentFieldPath;

                    if (parentFieldPath && allInstanceCounts) {
                      if (allInstanceCounts[parentFieldPath] !== undefined) {
                        count = allInstanceCounts[parentFieldPath];
                      } else {
                        let found = false;
                        for (const key of Object.keys(allInstanceCounts)) {
                          const schemaKey = key.replace(/\[\d+\]/g, "");
                          if (schemaKey === parentFieldPath) {
                            count = allInstanceCounts[key];
                            actualParentPath = key;
                            found = true;
                            break;
                          }
                        }
                        if (!found && parentFieldPath) {
                          const segments = parentFieldPath.split(".");
                          let currentSchemaPath = "";
                          let constructedPath = "";

                          segments.forEach((seg, index) => {
                            currentSchemaPath = currentSchemaPath
                              ? `${currentSchemaPath}.${seg}`
                              : seg;
                            const f = getFieldByPath(fields, currentSchemaPath);
                            const isSegMultiple =
                              f?.multiple === true ||
                              f?.multiple === "true" ||
                              (f?.children && f.children.length > 0);

                            constructedPath = constructedPath
                              ? `${constructedPath}.${seg}`
                              : seg;

                            if (isSegMultiple && index < segments.length - 1) {
                              constructedPath += "[0]";
                            }
                          });
                          actualParentPath = constructedPath;
                        }
                      }
                    }

                    const parentField = getFieldByPath(fields, parentFieldPath);
                    const isMultiple =
                      parentField?.multiple === true ||
                      parentField?.multiple === "true" ||
                      (parentField?.children && parentField.children.length > 0);

                    let nextValuePath;
                    let isEdit = false;

                    if (!isMultiple) {
                      nextValuePath = actualParentPath;
                      isEdit = true;
                    } else {
                      nextValuePath = `${actualParentPath}[${count}]`;
                      isEdit = false;
                    }

                    dispatch(
                      setChildFormNavigation({
                        nextValuePath,
                        parentPageIndex,
                        childPageIndex: i,
                        isEdit,
                        fallbackLabel: label,
                        fallbackFieldPath: actualParentPath,
                        useGeneratedPage: false,
                      }),
                    );

                    dispatch(setActivePage(i));
                    onNavigate?.();
                  } else {
                    dispatch(setActivePage(i));
                    dispatch(clearChildFormNavigation());
                    onNavigate?.();
                  }
                }}
                sx={{
                  pl: 2 + (page.depth || 0) * 2,
                  "&.Mui-selected": {
                    backgroundColor: theme.secondaryColor,
                    color: theme.primaryColor,
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: theme.secondaryColor,
                  },
                }}
              >
                <ListItemText primary={page.sidebarLabel} />
              </ListItemButton>
            );
          })}
        </List>
      ) : null}
    </Paper>
  );
}

export default FormSidebar;
